import React from "react";
import { observer } from "mobx-react";
import classnames from "classnames";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { roleLabels, RoleStats } from "../models/calc";
import { UnitEconomicsStoreType } from "../models/unit-economics-store";
import { percent, ratio, rubCompact, rubSign } from "./format";
import styles from "./UnitEconomicsPage.module.scss";

/** Цепочка показателей периода: выработка − труд − накладные = маржа, плюс отклонения */
export const KpiCards = observer(({ store }: { store: UnitEconomicsStoreType }) => {
    const total = store.totals;
    const threshold = store.data?.deviationThreshold ?? 15;
    const marginShare = total.output > 0 ? (total.margin / total.output) * 100 : null;

    return (
        <div className={styles.kpis}>
            <Kpi
                label="Выработка"
                value={rubCompact(total.output)}
                note={`${total.count} производственных сотрудников`}
                tip="Выручка проектов на рубль их производственных затрат × труд сотрудника на проекте"
                exact={rubSign(total.output)}
            />
            <span className={styles.kpiOperator}>−</span>
            <Kpi
                label="Себестоимость труда"
                value={rubCompact(total.factCost)}
                note="фактические затраты с налогами"
                exact={rubSign(total.factCost)}
            />
            <span className={styles.kpiOperator}>−</span>
            <Kpi
                label="Накладные"
                value={rubCompact(total.overhead)}
                note={`${ratio(store.overheadRate)} ₽ на 1 ₽ труда`}
                tip="Труд на проектах × переменная ставка накладных квартала (методика распределения накладных по проектам). Сумма по сотрудникам сходится с переменной частью пула накладных"
                exact={rubSign(total.overhead)}
            />
            <span className={styles.kpiOperator}>=</span>
            <Kpi
                label="Маржа"
                value={rubCompact(total.margin)}
                note={marginShare === null ? "—" : `${percent(marginShare)} от выработки`}
                exact={rubSign(total.margin)}
                tone={total.margin >= 0 ? "positive" : "negative"}
                accent
            />
            <button
                type="button"
                className={classnames(styles.kpi, styles.kpiAction, {
                    [styles.kpiActionOn]: store.onlyAnomalies,
                    [styles.kpiAlert]: store.anomalyCount > 0,
                })}
                onClick={store.toggleAnomalies}
                disabled={!store.onlyAnomalies && store.anomalyCount === 0}
                title={
                    store.onlyAnomalies
                        ? "Показать всех сотрудников"
                        : `Показать только сотрудников с отклонением стоимости часа больше ${threshold}%`
                }
            >
                <span className={styles.kpiLabel}>Отклонения &gt; {threshold}%</span>
                <span className={styles.kpiValue}>{store.anomalyCount}</span>
                <span className={classnames(styles.kpiNote, styles.kpiActionNote)}>
                    {store.onlyAnomalies ? "показать всех" : "показать только их"}
                </span>
            </button>
        </div>
    );
});

const Kpi = ({ label, value, note, tip, exact, tone, accent }: KpiProps) => (
    <div
        className={classnames(styles.kpi, {
            [styles.kpiAccentPositive]: accent && tone === "positive",
            [styles.kpiAccentNegative]: accent && tone === "negative",
        })}
        title={tip}
    >
        <span className={styles.kpiLabel}>{label}</span>
        <span
            className={classnames(styles.kpiValue, {
                [styles.positive]: tone === "positive",
                [styles.negative]: tone === "negative",
            })}
            title={exact}
        >
            {value}
        </span>
        <span className={styles.kpiNote}>{note}</span>
    </div>
);

interface KpiProps {
    label: string;
    value: string;
    note: string;
    tip?: string;
    exact?: string;
    tone?: "positive" | "negative";
    accent?: boolean;
}

/** Загрузка проектами по ролям против целевых значений */
export const BenchmarkPanel = observer(({ store, onEdit }: { store: UnitEconomicsStoreType; onEdit: () => void }) => (
    <div className={styles.benchmarks}>
        <div
            className={styles.benchmarksTitle}
            title="Часы на проектах ÷ (норма − отпуск − больничный − прочие уважительные отсутствия)"
        >
            <span>Загрузка проектами</span>
            <span className={styles.benchmarksHint}>часы на проектах ÷ доступные часы</span>
        </div>
        {store.roleStats.map((stats) => (
            <BenchmarkCard key={stats.role} stats={stats} />
        ))}
        <PlanrButton
            type="graybtn"
            size="small"
            icon="general-edit"
            round
            title="Изменить целевую загрузку"
            onClick={onEdit}
        />
    </div>
));

/** Карточка роли — только показатель; кого показать в таблице, выбирают фильтром над ней */
const BenchmarkCard = ({ stats }: { stats: RoleStats }) => {
    const value = stats.utilization;
    const tone = utilizationTone(value, stats.benchmark);
    const scale = Math.max(100, stats.benchmark, value ?? 0);

    return (
        <div className={classnames(styles.benchmark, { [styles.benchmarkEmpty]: !stats.count })}>
            <span className={styles.benchmarkHead}>
                <span className={styles.benchmarkRole}>{roleLabels[stats.role]}</span>
                <span className={styles.benchmarkCount}>{stats.count} чел.</span>
            </span>
            <span className={styles.benchmarkValues}>
                {stats.tracked ? (
                    <span className={classnames(styles.benchmarkValue, styles[tone])}>{percent(value)}</span>
                ) : (
                    <span className={styles.benchmarkNoData}>нет данных</span>
                )}
                <span className={styles.benchmarkTarget}>цель {percent(stats.benchmark)}</span>
            </span>
            <span className={styles.utilTrack}>
                <span
                    className={classnames(styles.utilFill, styles[tone])}
                    style={{ width: `${Math.min(100, ((value ?? 0) / scale) * 100)}%` }}
                />
                <span className={styles.utilMarker} style={{ left: `${(stats.benchmark / scale) * 100}%` }} />
            </span>
            <span className={styles.benchmarkNote}>
                {!stats.count
                    ? "нет сотрудников"
                    : !stats.tracked
                      ? "часы по проектам в табеле не отмечаются"
                      : stats.belowCount
                        ? `ниже цели: ${stats.belowCount} из ${stats.count}`
                        : "все на уровне цели"}
            </span>
        </div>
    );
};

export type UtilizationTone = "good" | "warn" | "bad" | "none";

/** Цвет загрузки: на уровне цели, чуть ниже (до 10 п.п.) или заметно ниже */
export function utilizationTone(value: number | null, benchmark: number): UtilizationTone {
    if (value === null) {
        return "none";
    }

    if (value >= benchmark) {
        return "good";
    }

    return value >= benchmark - 10 ? "warn" : "bad";
}
